import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { NAV, COMPANY } from "@/lib/content";
import { btn } from "@/components/ui-kit";

const Logo = () => (
  <Link to="/" className="flex items-center group" data-testid="header-logo">
    <img
      src="/redwood-logo.png"
      alt="Redwood Capital Holdings, Inc."
      className="h-14 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
    />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-line" : "bg-white/90 backdrop-blur-sm border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between gap-8 h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium tracking-wide transition-colors duration-200 hover:text-forest ${
                    isActive ? "text-forest" : "text-ink"
                  } after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/submit-property" className={btn.gold} data-testid="header-submit-property-btn">
              Submit a Property
            </Link>
          </div>

          <button
            className="lg:hidden text-forest p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-line" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-line/70 ${isActive ? "text-forest" : "text-ink"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/submit-property" className={`${btn.gold} mt-4 w-full`} data-testid="mobile-submit-property-btn">
              Submit a Property
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
